import { EnvironmentSettingsType, UserPreferenceType } from "services/settings/SettingsTypes";
import { ColorMode, Direction, Period, SchedulerMode, SchedulingView, ShowDays, TimeDisplayMode, ViewType } from "../SchedulingTypes";
import { generateTimeKeyFromTimeString } from "./schedulingDateUtils";
import { resolveAppointmentTextOptions } from "./appointmentText";

// Get the scheduling settings
export const getSchedulingSettings = (
    environmentSettings: EnvironmentSettingsType, 
    userPreferences: UserPreferenceType,
    displayDate: Date,
    period?: string,
    view?: SchedulingView | null,
    renderPersonalCalendarOnly?: boolean,
    activeFeatures?: string[]
): { 
    updatedView: SchedulingView | null
} => {
    // Return if environment settings are not given
    if (!environmentSettings) {
        return { updatedView: null };
    };

    // Get the personal scheduling settings from the user preferences
    const view_type = renderPersonalCalendarOnly ? 'personal_calendar' : (view?.viewType ?? userPreferences?.scheduling_view);
    const direction = userPreferences?.scheduling_direction;
    const display_days = userPreferences?.scheduling_display_mode;
    const color_mode = userPreferences?.scheduling_color_mode;
    // The job inbox only opens automatically while the job inbox feature is active
    const hasJobInboxFeature = activeFeatures ? activeFeatures.includes('job_inbox') : true;
    const show_job_inbox = hasJobInboxFeature && (userPreferences?.scheduling_auto_open_job_inbox ?? true);
    const show_profile_pictures = userPreferences?.scheduling_show_profile_pictures ?? true;
    const default_period = userPreferences?.scheduling_default_view_period;

    // Get the displayed times and other scheduling settings from the environment settings
    const {
        scheduling_display_start_time: display_start_time,
        scheduling_display_end_time: display_end_time,
        scheduling_time_blocks: render_time_blocks,
        scheduling_show_unassigned_row: show_unassigned_row,
        scheduling_appointment_text: appointment_text,
        job_create_jobs,
    } = environmentSettings;

    // Return if crucial settings are missing
    if (!view_type ||
        !direction ||
        !display_days ||
        !default_period
    ) return { updatedView: null };

    // Get the times to show based on the scheduling settings or fallback to default
    const showTimes = {
        startTime: generateTimeKeyFromTimeString(display_start_time || '06:00:00'),
        endTime: generateTimeKeyFromTimeString(display_end_time || '18:00:00')
    };

    // Set the scheduler mode
    let schedulerMode: SchedulerMode = view?.mode ?? (view_type === "project_planner" ? "projects" : "team");

    // Set the time display mode
    let timeDisplayMode: TimeDisplayMode = "none";
    if (view_type === "employee_timeline") {
        timeDisplayMode = "timeslots"
    } else if (view_type === "employee_list" && render_time_blocks) {
        timeDisplayMode = "timeblocks"
    };

    // Set the view with the settings
    const updatedView: SchedulingView = {
        mode: schedulerMode,
        viewType: view_type as ViewType,
        direction: direction as Direction,
        showDays: display_days as ShowDays,
        period: view?.period as Period ?? period as Period ?? default_period as Period,
        timeDisplayMode,
        renderTimeBlocks: render_time_blocks,
        colorMode: color_mode as ColorMode,
        showTimes,
        displayDate: view?.displayDate ?? displayDate,
        showProfilePictures: show_profile_pictures,
        showUnassignedResource: show_unassigned_row,
        showJobInbox: show_job_inbox,
        showSchedulerMap: view?.showSchedulerMap ?? false,
        createJobs: job_create_jobs,
        appointmentText: resolveAppointmentTextOptions(appointment_text),
    }

    return { updatedView };
};